import React from 'react';
import { Inbox, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * Friendly empty state with icon, message and optional CTAs.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title = 'Nothing here yet',
  description,
  action,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  className,
  compact = false,
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'py-8' : 'py-14', className)}>
      <div className="grid place-items-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-4">
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {(action || (actionLabel && onAction) || (secondaryLabel && onSecondary)) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action}
          {actionLabel && onAction && <Button onClick={onAction}>{actionLabel}</Button>}
          {secondaryLabel && onSecondary && (
            <Button variant="outline" onClick={onSecondary}>{secondaryLabel}</Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Error state with retry.
 */
export function ErrorState({
  title = 'Something went wrong',
  description = 'We couldn’t load this section. Please try again.',
  onRetry,
  className,
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
      <div className="grid place-items-center h-14 w-14 rounded-2xl bg-danger/10 text-danger mb-4">
        <AlertTriangle className="h-7 w-7" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{description}</p>
      {onRetry && (
        <Button variant="outline" className="mt-5" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      )}
    </div>
  );
}

/**
 * Shimmer skeleton block. Use for loading placeholders.
 */
export function SkeletonBlock({ className, style }) {
  return <div className={cn('shimmer rounded-md', className)} style={style} />;
}

/**
 * A card-shaped loading skeleton (header + rows).
 */
export function CardSkeleton({ rows = 4, className }) {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-5 space-y-3', className)}>
      <SkeletonBlock className="h-4 w-1/3" />
      <div className="space-y-2.5 pt-1">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonBlock key={i} className="h-3.5" style={{ width: `${90 - i * 8}%` }} />
        ))}
      </div>
    </div>
  );
}

/**
 * Grid of stat-card skeletons.
 */
export function StatSkeletonGrid({ count = 4, className }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-5">
          <SkeletonBlock className="h-3 w-1/2" />
          <SkeletonBlock className="mt-3 h-7 w-2/3" />
          <SkeletonBlock className="mt-3 h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}

export default EmptyState;
